/**
 * ScoutingReportRowContainer - Container for scouting report row
 *
 * Handles logic for a single scouting report row including binding the
 * press handler to the report ID and determining ownership via the
 * canEdit field from the backend.
 */

import { useCallback } from "react";

import type { ScoutingReport } from "@/features/player-detail/types";
import ScoutingReportRowView from "./ScoutingReportRowView";

/**
 * Props for ScoutingReportRowContainer component
 */
interface ScoutingReportRowContainerProps {
  /** The scouting report to display */
  readonly report: ScoutingReport;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Whether this is the last item in the list */
  readonly isLastItem: boolean;
  /** Callback when the row is pressed with report ID */
  readonly onPress: (reportId: string) => void;
}

/**
 * ScoutingReportRowContainer manages the logic for a scouting report row
 * @param props - Container props
 * @param props.report - The scouting report to display
 * @param props.isDark - Whether dark mode is active
 * @param props.isLastItem - Whether this is the last item in the list
 * @param props.onPress - Callback when the row is pressed with report ID
 * @returns ScoutingReportRowView component with bound handlers
 */
const ScoutingReportRowContainer = ({
  report,
  isDark,
  isLastItem,
  onPress,
}: ScoutingReportRowContainerProps) => {
  const isOwner = Boolean(report.canEdit);

  const handlePress = useCallback(() => {
    onPress(report.id);
  }, [report.id, onPress]);

  return (
    <ScoutingReportRowView
      report={report}
      isDark={isDark}
      isLastItem={isLastItem}
      onPress={handlePress}
      isOwner={isOwner}
    />
  );
};

ScoutingReportRowContainer.displayName = "ScoutingReportRowContainer";

export default ScoutingReportRowContainer;
